<script setup lang="ts">
import { computed } from "vue";
import { UiDivider, UiText } from "@shopexperts/shared";
import type { AdminProjectDetail } from "@shopexperts/legacy-api";

const props = defineProps<{
  project: AdminProjectDetail;
}>();

const briefText = computed(() => {
  return props.project.description || props.project.lead?.matching_strategy || null;
});

const rejectedByLabel = computed(() => {
  const rejectedBy = props.project.rejected_by;
  if (!rejectedBy) return null;
  return rejectedBy.name || rejectedBy.email || `User #${rejectedBy.id}`;
});

const rejectedAtLabel = computed(() => {
  if (!props.project.rejected_at) return null;
  return new Date(props.project.rejected_at).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
});

const rejectionMeta = computed(() =>
  [rejectedByLabel.value && `Rejected by ${rejectedByLabel.value}`, rejectedAtLabel.value]
    .filter(Boolean)
    .join(" · "),
);
</script>

<template>
  <div class="w-full space-y-4">
    <div
      v-if="props.project.rejection_reason"
      class="bg-white border border-gray-200 rounded-xl overflow-hidden px-7 py-6"
    >
      <UiText as="h3" variant="p" class="font-bold text-gray-900 tracking-[-0.01em]">
        Rejection Reason
      </UiText>
      <UiText v-if="rejectionMeta" as="p" variant="p-small" class="text-gray-500 mt-1">
        {{ rejectionMeta }}
      </UiText>

      <UiDivider spacing="sm" color="light" />

      <UiText as="p" variant="p" class="text-gray-700 leading-relaxed whitespace-pre-wrap mt-4">
        {{ props.project.rejection_reason }}
      </UiText>
    </div>

    <div class="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div class="px-7 pt-6" :class="props.project.attachments.length > 0 ? 'pb-2' : 'pb-6'">
        <UiText as="h3" variant="p" class="font-bold text-gray-900 tracking-[-0.01em]">
          Project Description
        </UiText>

        <UiDivider spacing="sm" color="light" />

        <UiText
          v-if="briefText"
          as="p"
          variant="p"
          class="text-gray-700 leading-relaxed whitespace-pre-wrap mt-4"
        >
          {{ briefText }}
        </UiText>

        <UiText
          v-else
          as="p"
          variant="p"
          class="text-gray-400 italic mt-4"
        >
          No description provided.
        </UiText>
      </div>

      <template v-if="props.project.attachments.length > 0">
        <div class="px-7 pt-6 pb-3">
          <UiText as="h3" variant="p" class="font-bold text-gray-900 tracking-[-0.01em]">
            Attached Files
          </UiText>
        </div>

        <div class="px-7 pb-6 space-y-2">
          <a
            v-for="att in props.project.attachments"
            :key="att.id"
            :href="att.cdn_url || att.url || '#'"
            target="_blank"
            rel="noopener"
            class="group flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-3.5 hover:border-gray-300 hover:bg-gray-50/60 transition-all"
          >
            <svg
              class="w-4 h-4 text-gray-400 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>

            <UiText as="span" variant="p-small" class="text-gray-800 flex-1 min-w-0 truncate">
              {{ att.filename }}
            </UiText>

            <svg
              class="w-4.5 h-4.5 text-gray-400 shrink-0 group-hover:text-gray-600 transition-colors"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </a>
        </div>
      </template>
    </div>
  </div>
</template>
